import { useState } from 'react'
import type { DimensionResult, DimensionId, StabilityType } from '@mindtype/shared'
import {
  DIMENSION_INFO,
  STABILITY_INTERPRETATIONS,
  getPoleInterpretation,
  getContextSwitchInsight,
} from '@mindtype/shared'

interface ColorConfig {
  primary: string
  secondary: string
  text: string
}

interface DimensionDetailsProps {
  dimensions: DimensionResult[]
  colorConfig?: ColorConfig
}

const STABILITY_LABELS: Record<StabilityType, string> = {
  stable: '稳定',
  work_adaptive: '工作适应',
  stress_switch: '压力切换',
  gradual: '渐进变化',
  flexible: '高弹性',
}

const CONTEXT_LABELS = {
  natural: '日常',
  work: '工作',
  stress: '压力',
}

// 默认配色
const DEFAULT_COLOR = { primary: '#3b82f6', secondary: '#60a5fa', text: '#ffffff' }

export function DimensionDetails({ dimensions, colorConfig = DEFAULT_COLOR }: DimensionDetailsProps) {
  const [expandedDimensions, setExpandedDimensions] = useState<Set<DimensionId>>(
    new Set(['D1', 'D2', 'D3', 'D4'])
  )

  const color = colorConfig

  const toggleDimension = (dimId: DimensionId) => {
    setExpandedDimensions(prev => {
      const next = new Set(prev)
      if (next.has(dimId)) {
        next.delete(dimId)
      } else {
        next.add(dimId)
      }
      return next
    })
  }

  const expandAll = () => setExpandedDimensions(new Set(['D1', 'D2', 'D3', 'D4']))
  const collapseAll = () => setExpandedDimensions(new Set())

  const allExpanded = expandedDimensions.size === 4
  const allCollapsed = expandedDimensions.size === 0

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold text-gray-900">四维度详情</h2>
        <div className="flex gap-2">
          <button
            onClick={expandAll}
            disabled={allExpanded}
            className="text-xs px-3 py-1 rounded transition-colors"
            style={{
              backgroundColor: `${color.primary}10`,
              color: allExpanded ? `${color.primary}50` : color.primary,
              cursor: allExpanded ? 'not-allowed' : 'pointer',
            }}
          >
            全部展开
          </button>
          <button
            onClick={collapseAll}
            disabled={allCollapsed}
            className="text-xs px-3 py-1 rounded transition-colors"
            style={{
              backgroundColor: `${color.primary}10`,
              color: allCollapsed ? `${color.primary}50` : color.primary,
              cursor: allCollapsed ? 'not-allowed' : 'pointer',
            }}
          >
            全部收起
          </button>
        </div>
      </div>

      {dimensions.map((dim) => {
        const dimInfo = DIMENSION_INFO[dim.dimensionId]
        const stabilityLabel = STABILITY_LABELS[dim.stability]
        const stabilityInfo = STABILITY_INTERPRETATIONS[dim.stability]
        const isExpanded = expandedDimensions.has(dim.dimensionId)

        const naturalPole = dim.natural.resultLetter
        const poleInterpretation = getPoleInterpretation(dim.dimensionId, naturalPole)

        const hasWorkSwitch = dim.natural.resultLetter !== dim.work.resultLetter
        const hasStressSwitch = dim.work.resultLetter !== dim.stress.resultLetter

        return (
          <div
            key={dim.dimensionId}
            className="bg-white rounded-xl overflow-hidden"
            style={{ border: `1px solid ${color.primary}15` }}
          >
            {/* 头部 */}
            <button
              onClick={() => toggleDimension(dim.dimensionId)}
              className="w-full p-5 text-left transition-colors"
            >
              <div className="flex justify-between items-start">
                <div className="flex items-center gap-3">
                  <span
                    className="w-9 h-9 rounded-lg flex items-center justify-center text-sm font-semibold"
                    style={{ backgroundColor: `${color.primary}15`, color: color.primary }}
                  >
                    {dim.dimensionId}
                  </span>
                  <div>
                    <h3 className="font-medium text-gray-900">{dimInfo.name}</h3>
                    <p className="text-sm text-gray-500">{dimInfo.description}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span
                    className="px-2 py-1 rounded text-xs font-medium"
                    style={{ backgroundColor: `${color.primary}10`, color: color.primary }}
                  >
                    {stabilityLabel}
                  </span>
                  <svg
                    className="w-5 h-5 transition-transform"
                    style={{ color: color.primary, transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>

              {/* 三层情境得分 */}
              <div className="grid grid-cols-3 gap-3 mt-4">
                {(['natural', 'work', 'stress'] as const).map((context) => {
                  const score = dim[context]
                  const total = score.poleACount + score.poleBCount
                  const poleAPercent = total > 0 ? (score.poleACount / total) * 100 : 50

                  return (
                    <div
                      key={context}
                      className="text-center p-3 rounded-lg"
                      style={{ backgroundColor: `${color.primary}08` }}
                    >
                      <div className="text-xs mb-1" style={{ color: color.primary }}>{CONTEXT_LABELS[context]}</div>
                      <div className="text-xl font-semibold text-gray-900">{score.resultLetter}</div>
                      <div className="text-xs text-gray-400 mb-2">{score.poleACount} : {score.poleBCount}</div>
                      <div
                        className="h-1 rounded-full overflow-hidden"
                        style={{ backgroundColor: `${color.primary}15` }}
                      >
                        <div
                          className="h-full rounded-full transition-all"
                          style={{ width: `${poleAPercent}%`, backgroundColor: color.primary }}
                        />
                      </div>
                      {score.isWeak && (
                        <div className="text-xs mt-1" style={{ color: `${color.primary}80` }}>弱倾向</div>
                      )}
                    </div>
                  )
                })}
              </div>
            </button>

            {/* 展开内容 */}
            {isExpanded && (
              <div className="px-5 pb-5" style={{ borderTop: `1px solid ${color.primary}15` }}>
                {/* 日常倾向解读 */}
                {poleInterpretation && (
                  <div className="mt-4">
                    <h4 className="text-sm font-medium mb-3" style={{ color: color.primary }}>
                      你的日常倾向：{naturalPole === dimInfo.poleA.letter ? dimInfo.poleA.name : dimInfo.poleB.name}
                    </h4>
                    <p className="text-sm text-gray-600 mb-4">
                      {poleInterpretation.summary}
                    </p>

                    {/* 典型表现 */}
                    <div className="mb-4">
                      <div className="text-xs font-medium mb-2" style={{ color: color.primary }}>典型表现</div>
                      <ul className="space-y-1.5">
                        {poleInterpretation.behaviors.map((behavior, i) => (
                          <li key={i} className="text-sm text-gray-600 flex items-start gap-2">
                            <span style={{ color: `${color.primary}80` }} className="mt-1">•</span>
                            {behavior}
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* 优势与挑战 */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-3 rounded-lg" style={{ backgroundColor: `${color.primary}08` }}>
                        <div className="text-xs font-medium mb-2" style={{ color: color.primary }}>优势</div>
                        <ul className="space-y-1">
                          {poleInterpretation.strengths.map((s, i) => (
                            <li key={i} className="text-xs text-gray-600 flex items-start gap-1">
                              <span style={{ color: color.primary }}>•</span>
                              {s}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="p-3 rounded-lg" style={{ backgroundColor: `${color.secondary}10` }}>
                        <div className="text-xs font-medium mb-2" style={{ color: color.secondary }}>潜在挑战</div>
                        <ul className="space-y-1">
                          {poleInterpretation.challenges.map((c, i) => (
                            <li key={i} className="text-xs text-gray-600 flex items-start gap-1">
                              <span style={{ color: color.secondary }}>•</span>
                              {c}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}

                {/* 情境切换 */}
                {(hasWorkSwitch || hasStressSwitch) && (
                  <div className="mt-4">
                    <h4 className="text-sm font-medium mb-3" style={{ color: color.primary }}>
                      情境切换
                    </h4>
                    <div className="space-y-2">
                      {hasWorkSwitch && (
                        <div
                          className="p-3 rounded-lg"
                          style={{ backgroundColor: `${color.primary}08`, border: `1px solid ${color.primary}15` }}
                        >
                          <div className="text-sm text-gray-600">
                            {getContextSwitchInsight(
                              dim.dimensionId,
                              dim.natural.resultLetter,
                              dim.work.resultLetter,
                              'work'
                            ).insight}
                          </div>
                        </div>
                      )}
                      {hasStressSwitch && (
                        <div
                          className="p-3 rounded-lg"
                          style={{ backgroundColor: `${color.primary}08`, border: `1px solid ${color.primary}15` }}
                        >
                          <div className="text-sm text-gray-600">
                            {getContextSwitchInsight(
                              dim.dimensionId,
                              dim.work.resultLetter,
                              dim.stress.resultLetter,
                              'stress'
                            ).insight}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* 稳定性解读 */}
                <div className="mt-4">
                  <div className="p-3 rounded-lg" style={{ backgroundColor: `${color.primary}08` }}>
                    <div className="text-sm text-gray-600">
                      <span className="font-medium" style={{ color: color.primary }}>{stabilityInfo.name}：</span>
                      {stabilityInfo.description}
                    </div>
                  </div>
                </div>

                {/* 极点说明 */}
                <div
                  className="flex justify-between mt-4 pt-3 text-xs text-gray-500"
                  style={{ borderTop: `1px solid ${color.primary}15` }}
                >
                  <span className="flex items-center gap-1">
                    <span
                      className="w-5 h-5 rounded flex items-center justify-center font-medium"
                      style={{ backgroundColor: `${color.primary}15`, color: color.primary }}
                    >
                      {dimInfo.poleA.letter}
                    </span>
                    {dimInfo.poleA.name}（{dimInfo.poleA.shortDesc}）
                  </span>
                  <span className="flex items-center gap-1">
                    <span
                      className="w-5 h-5 rounded flex items-center justify-center font-medium"
                      style={{ backgroundColor: `${color.primary}15`, color: color.primary }}
                    >
                      {dimInfo.poleB.letter}
                    </span>
                    {dimInfo.poleB.name}（{dimInfo.poleB.shortDesc}）
                  </span>
                </div>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
